import {useCallback, useEffect, useMemo, useRef} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Skeleton,
  Spinner,
  Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {getProductEffectivePrice, getProductListPrice} from '@amblique/king-living/app/utils/product-utils';
import {handleRadioTileKeyDown} from '@amblique/king-living/app/utils/radio-keyboard';
import {useProductSlotData} from '@amblique/king-living/app/hooks/use-product-slot-data';
import {RadioOptionContent} from '../radio-option-content';

/**
 * Option-slot list: row click = preview only; parent commits draft on Next/Done.
 * Auto-preselected SKU previews only until Next. `highlightedProductId` drives radio UI (preview or draft).
 * Visual treatment matches Complex PDP customize steps (e.g. Choose your shelves).
 */
export function OptionSlotProductPicker({
  container,
  customizeStepKind = 'other',
  highlightedProductId,
  isSimplePDP = false,
  onSlotProductPreview,
  onViewDetails,
  selections,
  slotIndex,
  styles,
  value,
}) {
  const currentSelection = selections?.[slotIndex] ?? null;
  const highlightId = highlightedProductId ?? currentSelection?.productId ?? null;

  const handleAutoSelect = useCallback(
    (product) => {
      onSlotProductPreview?.(product);
    },
    [onSlotProductPreview]
  );

  // Suppress BM preselection in Simple PDP flyouts (FSD §4.1 — "no option will be
  // pre-selected by default"). Complex PDP keeps auto-preview for isPreselected SKUs.
  const {products, isLoading, isLoadingMore} = useProductSlotData(value, currentSelection, handleAutoSelect, {
    disablePreselect: isSimplePDP,
  });

  // Sync gallery when draft row resolves to a loaded product (re-entry / second flyout open).
  const onSlotProductPreviewRef = useRef(onSlotProductPreview);

  useEffect(() => {
    onSlotProductPreviewRef.current = onSlotProductPreview;
  });

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === currentSelection?.productId) ?? null,
    [products, currentSelection?.productId]
  );

  useEffect(() => {
    if (!selectedProduct) return;
    onSlotProductPreviewRef.current?.(selectedProduct);
  }, [selectedProduct]);

  if (!container || !value) {
    return null;
  }

  return (
    <Box className="option-slot-product-picker-container">
      {isLoading ? (
        <Box>
          <Skeleton height="80px" marginBottom={2} />
          <Skeleton height="80px" marginBottom={2} />
          <Skeleton height="80px" />
        </Box>
      ) : products.length > 0 ? (
        <Box aria-busy={isLoadingMore} className="option-slot-product-picker-list-wrapper">
          <Box
            role="radiogroup"
            {...styles.optionStepListRadio}
            {...(isLoadingMore ? styles.optionStepListLocked : {})}
            className={[
              'option-slot-product-picker-list',
              'option-step-list',
              'option-step-list__radio',
              isSimplePDP && 'option-step-list__radio--simple-pdp-flyout'
            ].filter(Boolean).join(' ')}
          >
            {products.map((product) => {
              const isHighlighted = highlightId != null && product.id === highlightId;
              const extraPrice = getProductEffectivePrice(product);
              const productListPrice = getProductListPrice(product);
              const handlePreview = () => onSlotProductPreview?.(product);

              return (
                <Box
                  key={product.id}
                  aria-checked={isHighlighted}
                  aria-disabled={isLoadingMore}
                  marginBottom={isSimplePDP ? 0 : 2}
                  role="radio"
                  tabIndex={isLoadingMore ? -1 : 0}
                  {...styles.radioOption}
                  {...(isHighlighted ? styles.radioOptionSelected : {})}
                  className={[
                    'option-slot-product-picker-item',
                    'option-inner-radio-item',
                    isHighlighted && 'option-inner-radio-item--selected'
                  ].filter(Boolean).join(' ')}
                  onClick={() => !isLoadingMore && handlePreview()}
                  onKeyDown={(event) => handleRadioTileKeyDown(event, isLoadingMore, handlePreview)}
                >
                  <RadioOptionContent
                    containerId={container.containerId}
                    extraPrice={extraPrice > 0 ? extraPrice : undefined}
                    isSelected={isHighlighted}
                    listPrice={productListPrice}
                    product={product}
                    styles={styles}
                    useCushionSingleTitle={customizeStepKind === 'cushion'}
                    isFlyoutPicker
                    onViewDetails={isLoadingMore ? undefined : onViewDetails}
                  />
                </Box>
              );
            })}
          </Box>
          {isLoadingMore && (
            <Flex
              aria-live="polite"
              as="div"
              className="option-slot-product-picker-loading-more"
              {...styles.optionStepLoadingMore}
            >
              <Spinner {...styles.optionStepLoadingMoreSpinner} />
              <Text {...styles.optionStepLoadingMoreText}>
                <FormattedMessage
                  defaultMessage="Loading more"
                  id="customize_panel.option_step.loading_more"
                />
              </Text>
            </Flex>
          )}
        </Box>
      ) : (
        <Text color="gray.500" fontSize="sm">
          <FormattedMessage
            defaultMessage="No products available"
            id="customize_panel.option_step.no_products"
          />
        </Text>
      )}
    </Box>
  );
}

OptionSlotProductPicker.displayName = 'OptionSlotProductPicker';

OptionSlotProductPicker.propTypes = {
  container: PropTypes.shape({
    containerId: PropTypes.string.isRequired,
    containerName: PropTypes.string,
    values: PropTypes.array.isRequired,
  }).isRequired,
  customizeStepKind: PropTypes.string,
  highlightedProductId: PropTypes.string,
  isSimplePDP: PropTypes.bool,
  onSlotProductPreview: PropTypes.func,
  onViewDetails: PropTypes.func,
  selections: PropTypes.array,
  slotIndex: PropTypes.number.isRequired,
  styles: PropTypes.object.isRequired,
  value: PropTypes.shape({
    isRequired: PropTypes.bool,
    isVisible: PropTypes.bool,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        isHidden: PropTypes.bool,
        isPreselected: PropTypes.bool,
        productId: PropTypes.string.isRequired,
      })
    ),
    recommendationTypeId: PropTypes.number,
    typeName: PropTypes.string,
  }).isRequired,
};
